import React, { useEffect, useState } from 'react';
import { fetchWhatsOn } from '../services/whatsOn';

/**
 * What's On — venue events + promotions on the guest homepage (ClientInfo).
 * Reads the same `muuzboxEvents` docs the admin Event Calendar and Ticket
 * Sales write, plus `venuePromotions` from the Promotions surface.
 * Renders nothing at all when the venue has published nothing.
 *
 * Product-UI palette per the Muuzbox brand kit — explicit hexes, since this
 * app's Tailwind build still carries stock pink/violet shades.
 */
const MAGENTA = '#E8007D';
const VIOLET = '#7C3AED';
const CARD_BG = 'rgba(255,255,255,0.04)';
const CARD_BORDER = 'rgba(255,255,255,0.08)';
const TEXT_MUTED = '#8892A4';

const dateChip = (ms) => {
  if (!ms) return null;
  const d = new Date(ms);
  return {
    day: d.getDate(),
    mon: d.toLocaleDateString('en-GB', { month: 'short' }),
    time: d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
  };
};

const WhatsOn = ({ clientObj }) => {
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    let dead = false;
    if (!clientObj) { setFeed(null); return undefined; }
    fetchWhatsOn(clientObj)
      .then((f) => { if (!dead) setFeed(f); })
      .catch(() => { if (!dead) setFeed({ events: [], promos: [] }); });
    return () => { dead = true; };
  }, [clientObj?.clientId, clientObj?.id]);

  if (!feed || (feed.events.length === 0 && feed.promos.length === 0)) return null;

  return (
    <div className="post text-left text-white" style={{ marginBottom: 16 }}>
      <p className="title text-white" style={{ fontSize: 18, fontWeight: 700 }}>
        <strong>What&rsquo;s On</strong>
      </p>

      {/* Promotions — the venue's live specials, magenta-edged banners */}
      {feed.promos.map((p) => (
        <div
          key={p.id}
          style={{
            background: CARD_BG,
            border: `1px solid ${CARD_BORDER}`,
            borderLeft: `4px solid ${MAGENTA}`,
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 10,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700 }}>📣 {p.title}</div>
          {p.message && (
            <div style={{ fontSize: 16, color: '#E8EAF0', marginTop: 4 }}>{p.message}</div>
          )}
        </div>
      ))}

      {/* Upcoming events */}
      {feed.events.map((e) => {
        const chip = dateChip(e.startsAtMs);
        return (
          <div
            key={e.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              background: CARD_BG,
              border: `1px solid ${CARD_BORDER}`,
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                minWidth: 56,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${MAGENTA}, ${VIOLET})`,
                borderRadius: 10,
                padding: '6px 8px',
              }}
            >
              {chip ? (
                <>
                  <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1 }}>{chip.day}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase' }}>{chip.mon}</div>
                </>
              ) : (
                <div style={{ fontSize: 20 }}>🎉</div>
              )}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{e.name}</div>
              <div style={{ fontSize: 15, color: TEXT_MUTED, marginTop: 2 }}>
                {chip ? `${chip.time}` : ''}
                {e.dateText ? `${chip ? ' · ' : ''}${e.dateText}` : ''}
              </div>
              {e.description && (
                <div
                  style={{
                    fontSize: 15,
                    color: '#E8EAF0',
                    marginTop: 4,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {e.description}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WhatsOn;
